import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import contractTypeMasterService from '../../services/contractTypeMasterService'

const ContractTypeMasterForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isEditMode = location.pathname.includes('/edit')
  const isViewMode = location.pathname.includes('/view')
  const typeData = location.state?.typeData

  const [formData, setFormData] = useState({
    contractTypeCode: '',
    contractTypeName: '',
    contractTypeDescription: '',
    category: 'Service',
    defaultSettings: {
      requiresLegalReview: false,
      requiresApproval: true,
      defaultDuration: 12,
      defaultRenewalPeriod: 30,
      isRenewable: true,
      autoRenewal: false
    },
    templateFields: [],
    status: 'Active',
    notes: ''
  })

  const [isLoadingContractTypeCode, setIsLoadingContractTypeCode] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  // Fetch next contract type code (only for new types)
  useEffect(() => {
    if (!isEditMode && !isViewMode) {
      const fetchContractTypeCode = async () => {
        try {
          setIsLoadingContractTypeCode(true)
          const response = await contractTypeMasterService.getNextContractTypeCode()
          if (response.success && response.data) {
            setFormData(prev => ({
              ...prev,
              contractTypeCode: response.data.contractTypeCode || response.data.formattedValue || 'CT0001'
            }))
          }
        } catch (error) {
          console.error('Error fetching contract type code:', error)
          setFormData(prev => ({ ...prev, contractTypeCode: 'CT0001' }))
        } finally {
          setIsLoadingContractTypeCode(false)
        }
      }
      fetchContractTypeCode()
    }
  }, [isEditMode, isViewMode])

  // Load contract type data if editing or viewing
  useEffect(() => {
    if ((isEditMode || isViewMode) && typeData?._id) {
      loadTypeData(typeData._id)
    }
  }, [isEditMode, isViewMode, typeData])

  const loadTypeData = async (id) => {
    try {
      setIsLoading(true)
      const response = await contractTypeMasterService.getContractTypeById(id)
      if (response.success && response.data) {
        const type = response.data
        setFormData({
          contractTypeCode: type.contractTypeCode || '',
          contractTypeName: type.contractTypeName || '',
          contractTypeDescription: type.contractTypeDescription || '',
          category: type.category || 'Service',
          defaultSettings: type.defaultSettings || {
            requiresLegalReview: false,
            requiresApproval: true,
            defaultDuration: 12,
            defaultRenewalPeriod: 30,
            isRenewable: true,
            autoRenewal: false
          },
          templateFields: type.templateFields || [],
          status: type.status || 'Active',
          notes: type.notes || ''
        })
      }
    } catch (error) {
      console.error('Error loading contract type data:', error)
      toast.error('Failed to load contract type data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    
    if (name.startsWith('defaultSettings.')) {
      const settingField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        defaultSettings: {
          ...prev.defaultSettings,
          [settingField]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
        }
      }))
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }))
    } else if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: parseFloat(value) || 0
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.contractTypeName) {
        toast.error('Please fill in all required fields (Contract Type Name)')
        return
      }

      setIsLoading(true)
      
      if (isEditMode && typeData?._id) {
        const response = await contractTypeMasterService.updateContractType(typeData._id, formData)
        if (response.success) {
          toast.success('Contract type updated successfully!')
          navigate('/contracts/contract-type-master')
        } else {
          toast.error(response.message || 'Failed to update contract type')
        }
      } else {
        const response = await contractTypeMasterService.createContractType(formData)
        if (response.success) {
          toast.success('Contract type created successfully!')
          navigate('/contracts/contract-type-master')
        } else {
          toast.error(response.message || 'Failed to create contract type')
        }
      }
    } catch (error) {
      console.error('Error saving contract type:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to save contract type')
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    if (isEditMode || isViewMode) {
      loadTypeData(typeData._id)
    } else {
      setFormData(prev => ({
        ...prev,
        contractTypeName: '',
        contractTypeDescription: '',
        category: 'Service',
        defaultSettings: {
          requiresLegalReview: false,
          requiresApproval: true,
          defaultDuration: 12,
          defaultRenewalPeriod: 30,
          isRenewable: true,
          autoRenewal: false
        },
        templateFields: [],
        status: 'Active',
        notes: ''
      }))
    }
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Contracts', path: '/contracts' },
    { label: 'Contract Types', path: '/contracts/contract-type-master' },
    { 
      label: isEditMode ? 'Edit Contract Type' : isViewMode ? 'View Contract Type' : 'New Contract Type', 
      path: location.pathname, 
      isCurrent: true 
    }
  ]

  if (isLoading && (isEditMode || isViewMode)) {
    return (
      <div className="sales-dashboard">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading contract type...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Contract Type' : isViewMode ? 'View Contract Type' : 'New Contract Type'}</h1>
          <p>{isEditMode ? 'Update contract type details' : isViewMode ? 'View contract type details' : 'Create a new contract type'}</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/contracts/contract-type-master')}
            style={{
              padding: '10px 20px',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              color: '#495057'
            }}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Contract Type Information</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header"><h3>Basic Information</h3></div>
          {/* Row 1: Contract Type Code, Contract Type Name, Category, Status */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="contractTypeCode" className="form-label required">
                Contract Type Code
              </label>
              <input
                type="text"
                id="contractTypeCode"
                name="contractTypeCode"
                value={isLoadingContractTypeCode ? 'Generating...' : formData.contractTypeCode}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="contractTypeName" className="form-label required">
                Contract Type Name
              </label>
              <input
                type="text"
                id="contractTypeName"
                name="contractTypeName"
                value={formData.contractTypeName}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                required
                placeholder="e.g., Service Agreement"
              />
            </div>

            <div className="form-group">
              <label htmlFor="category" className="form-label">
                Category
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Service">Service</option>
                <option value="Legal">Legal</option>
                <option value="Commercial">Commercial</option>
                <option value="Partnership">Partnership</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Contract Type Description */}
          <div className="form-group">
            <label htmlFor="contractTypeDescription" className="form-label">
              Description
            </label>
            <textarea
              id="contractTypeDescription"
              name="contractTypeDescription"
              value={formData.contractTypeDescription}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Brief description of the contract type..."
            />
          </div>

          {/* Default Settings */}
          <div className="section-header"><h3>Default Settings</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="defaultSettings.requiresLegalReview" className="form-label">
                Requires Legal Review
              </label>
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                <input
                  type="checkbox"
                  id="defaultSettings.requiresLegalReview"
                  name="defaultSettings.requiresLegalReview"
                  checked={formData.defaultSettings.requiresLegalReview}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={{ width: '18px', height: '18px', cursor: isViewMode ? 'not-allowed' : 'pointer' }}
                />
                <label htmlFor="defaultSettings.requiresLegalReview" style={{ marginLeft: '8px', fontSize: '14px', color: '#495057' }}>
                  Legal review required
                </label>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="defaultSettings.requiresApproval" className="form-label">
                Requires Approval
              </label>
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                <input
                  type="checkbox"
                  id="defaultSettings.requiresApproval"
                  name="defaultSettings.requiresApproval"
                  checked={formData.defaultSettings.requiresApproval}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={{ width: '18px', height: '18px', cursor: isViewMode ? 'not-allowed' : 'pointer' }}
                />
                <label htmlFor="defaultSettings.requiresApproval" style={{ marginLeft: '8px', fontSize: '14px', color: '#495057' }}>
                  Approval required
                </label>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="defaultSettings.defaultDuration" className="form-label">
                Default Duration (Months)
              </label>
              <input
                type="number"
                id="defaultSettings.defaultDuration"
                name="defaultSettings.defaultDuration"
                value={formData.defaultSettings.defaultDuration}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="defaultSettings.defaultRenewalPeriod" className="form-label">
                Default Renewal Period (Days)
              </label>
              <input
                type="number"
                id="defaultSettings.defaultRenewalPeriod"
                name="defaultSettings.defaultRenewalPeriod"
                value={formData.defaultSettings.defaultRenewalPeriod}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
              />
            </div>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="defaultSettings.isRenewable" className="form-label">
                Is Renewable
              </label>
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                <input
                  type="checkbox"
                  id="defaultSettings.isRenewable"
                  name="defaultSettings.isRenewable"
                  checked={formData.defaultSettings.isRenewable}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={{ width: '18px', height: '18px', cursor: isViewMode ? 'not-allowed' : 'pointer' }}
                />
                <label htmlFor="defaultSettings.isRenewable" style={{ marginLeft: '8px', fontSize: '14px', color: '#495057' }}>
                  Contract is renewable
                </label>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="defaultSettings.autoRenewal" className="form-label">
                Auto Renewal
              </label>
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                <input
                  type="checkbox"
                  id="defaultSettings.autoRenewal"
                  name="defaultSettings.autoRenewal"
                  checked={formData.defaultSettings.autoRenewal}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={{ width: '18px', height: '18px', cursor: isViewMode ? 'not-allowed' : 'pointer' }}
                />
                <label htmlFor="defaultSettings.autoRenewal" style={{ marginLeft: '8px', fontSize: '14px', color: '#495057' }}>
                  Auto-renew contract
                </label>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="form-group">
            <label htmlFor="notes" className="form-label">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Additional notes..."
            />
          </div>

          {/* Action Buttons */}
          {!isViewMode && (
            <div className="form-actions">
              <button
                type="button"
                onClick={handleSave}
                disabled={isLoading}
                className="btn btn-primary"
                style={{
                  padding: '10px 24px',
                  background: '#292752',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isLoading ? 0.6 : 1
                }}
              >
                <Save size={16} />
                {isLoading ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={isLoading}
                className="btn btn-secondary"
                style={{
                  padding: '10px 24px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  background: '#fff',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  color: '#495057'
                }}
              >
                <RotateCcw size={16} />
                Reset
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default ContractTypeMasterForm

